import { useEffect, useRef } from "react"
import { twMerge } from "tailwind-merge"
import Card, { CardProps } from "../Card"
import SkeletonCard from "./skeletonCard"

export type ColumnProps = {
  className?: string
  title: string
  headerButton?: JSX.Element
  cards?: CardProps['card'][]
  onClickOnCard?: (x: string) => void
  isPending?: boolean
  skeletonCount?: number
  dataTestid?: string
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => void
}

const Column = ({
  className,
  title,
  headerButton,
  cards = [],
  onClickOnCard,
  isPending,
  skeletonCount,
  dataTestid,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: ColumnProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer || !fetchNextPage) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer

      if (scrollHeight - scrollTop <= clientHeight + 50) {
        if (hasNextPage && !isFetchingNextPage && !isPending) {
          fetchNextPage()
        }
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [hasNextPage, isFetchingNextPage, isPending, fetchNextPage])

  return (
    <div className={twMerge('w-[220px] border border-border-default mb-[10px] rounded-lg', className)}>
      <div className={twMerge('w-[220px] p-4 bg-white-100 flex ', headerButton ? 'justify-between' : 'justify-start')}>
        <p className="font-bold text-sm h-[30px]">{title}</p>
        {!isPending && headerButton ? headerButton : null}
      </div>
      <div
        ref={scrollContainerRef}
        data-testid={dataTestid}
        className="p-4 flex flex-col gap-4 h-[calc(100vh-335px)] w-[220px] overflow-y-auto thin-scroll"
      >
        {isPending ? (
          Array(skeletonCount || 10)
            .fill(0)
            .map((_, index) => <SkeletonCard key={index} />)
        ) : (
          <>
            {cards.map((card) => (
              <Card key={card.id} card={card} onClick={onClickOnCard} />
            ))}
            {isFetchingNextPage && <SkeletonCard />}
          </>
        )}
      </div>
    </div>
  )
}

export default Column
